import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    BarChart2,
    TrendingUp,
    User,
    MessageSquare,
    AlertTriangle,
    Gavel,
    CheckCircle,
    Clock,
    Calendar,
    Award,
    Star,
    Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { getStaffDisplayName } from '../../utils/staffDisplay';

const StaffStatistics = () => {
    const { user, isAdmin, isHead } = useAuth();
    const staffName = getStaffDisplayName(user);
    const { tickets } = useTickets();
    const { getAllPunishments } = usePunishment();

    // Data Aggregation
    const stats = useMemo(() => {
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        const allPunishments = getAllPunishments();

        // Personal Stats
        const myTickets = tickets.filter(t => t.assignedTo === user.discordId || (t.responses && t.responses.some(r => r && (r.author === user.name || r.author === staffName) && r.isStaff)));
        const myTickets24h = myTickets.filter(t => new Date(t.lastUpdate || t.createdAt) > oneDayAgo);

        const myPunishments = allPunishments.filter(p => p.appliedBy === user.name || p.appliedBy === staffName);
        const myPunishments24h = myPunishments.filter(p => new Date(p.appliedAt) > oneDayAgo);

        const myEscalations = tickets.filter(t => t.escalatedBy === user.discordId);

        // New Metrics Calculations
        let totalMessagesSent = 0;
        const categoryCount = {};
        let solvedCount = 0;

        myTickets.forEach(t => {
            // Messages
            if (t.responses) {
                totalMessagesSent += t.responses.filter(r => r && ((r.author === user.name || r.author === staffName) || (r.isStaff && (r.author === user.name || r.author === staffName)))).length;
            }
            // Categories
            if (t.category) {
                categoryCount[t.category] = (categoryCount[t.category] || 0) + 1;
            }
            // Solved Ratio
            if (t.status === 'resolvido' || t.status === 'fechado') {
                solvedCount++;
            }
        });

        // Top Category
        let topCategory = 'Nenhuma';
        let maxCount = 0;
        Object.entries(categoryCount).forEach(([cat, count]) => {
            if (count > maxCount) {
                maxCount = count;
                topCategory = cat;
            }
        });

        // Format Top Category (capitalize/clean)
        topCategory = topCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        // Resolution Rate
        const resolutionRate = myTickets.length > 0 ? Math.round((solvedCount / myTickets.length) * 100) : 0;

        // Recent Actions (Last 5 tickets updated)
        const recentActions = [...myTickets]
            .sort((a, b) => new Date(b.lastUpdate || b.createdAt) - new Date(a.lastUpdate || a.createdAt))
            .slice(0, 5);

        // Global Stats (for High Rank)
        const totalTickets = tickets.length;
        const totalOpen = tickets.filter(t => t.status === 'aberto').length;
        const totalSolved24h = tickets.filter(t => (t.status === 'resolvido' || t.status === 'fechado') && new Date(t.lastUpdate) > oneDayAgo).length;

        return {
            personal: {
                totalTickets: myTickets.length,
                tickets24h: myTickets24h.length,
                punishments: myPunishments.length,
                punishments24h: myPunishments24h.length,
                escalations: myEscalations.length,
                messagesSent: totalMessagesSent,
                resolutionRate,
                topCategory,
                recentActions
            },
            global: {
                totalTickets,
                totalOpen,
                totalSolved24h,
                totalPunishments24h: allPunishments.filter(p => new Date(p.appliedAt) > oneDayAgo).length
            }
        };
    }, [tickets, user, getAllPunishments, staffName]);

    const StatCard = ({ title, value, subValue, icon: Icon, color, delay }) => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
        >
            <Card className="bg-[#0f1115]/80 backdrop-blur border border-white/10 hover:border-white/20 transition-all group overflow-hidden relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${color} shadow-lg`}>
                            <Icon className="h-6 w-6 text-white" />
                        </div>
                        {subValue && (
                            <Badge variant="outline" className="bg-white/5 border-white/10 text-gray-400">
                                {subValue}
                            </Badge>
                        )}
                    </div>
                    <div>
                        <h3 className="text-sm font-medium text-gray-400 mb-1">{title}</h3>
                        <div className="flex items-baseline gap-2">
                            <span className="text-2xl md:text-3xl font-bold text-white break-words leading-tight" title={value}>{value}</span>
                            {/* Logic for +24h subvalue if needed */}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );

    return (
        <div className="space-y-8 pb-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <BarChart2 className="h-8 w-8 text-purple-500" />
                        Estatísticas & Performance
                    </h1>
                    <p className="text-gray-400 mt-1">
                        Acompanhe seu desempenho e métricas em tempo real
                    </p>
                </div>
                <div className="flex items-center gap-3 bg-[#0f1115] border border-white/10 rounded-xl px-4 py-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-300">
                        {new Date().toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                </div>
            </div>

            {/* Personal Stats Grid */}
            <div>
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-400" />
                    Seu Desempenho
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        title="Tickets Atendidos"
                        value={stats.personal.totalTickets}
                        subValue={`+${stats.personal.tickets24h} (24h)`}
                        icon={MessageSquare}
                        color="from-blue-600 to-indigo-600"
                        delay={0.1}
                    />
                    <StatCard
                        title="Punições Aplicadas"
                        value={stats.personal.punishments}
                        subValue={`+${stats.personal.punishments24h} (24h)`}
                        icon={Gavel}
                        color="from-red-600 to-rose-600"
                        delay={0.2}
                    />
                    <StatCard
                        title="Taxa de Resolução"
                        value={`${stats.personal.resolutionRate}%`}
                        icon={CheckCircle}
                        color="from-green-600 to-emerald-600"
                        delay={0.3}
                    />
                    <StatCard
                        title="Mensagens Enviadas"
                        value={stats.personal.messagesSent}
                        icon={MessageSquare}
                        color="from-purple-600 to-violet-600"
                        delay={0.4}
                    />
                    <StatCard
                        title="Top Categoria"
                        value={stats.personal.topCategory}
                        icon={Star}
                        color="from-yellow-500 to-amber-500"
                        delay={0.5}
                    />
                    <StatCard
                        title="Escalações"
                        value={stats.personal.escalations}
                        icon={AlertTriangle}
                        color="from-orange-600 to-amber-600"
                        delay={0.6}
                    />
                </div>
            </div>

            {/* Recent Actions List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-[#0f1115]/80 backdrop-blur border border-white/10 h-full">
                    <CardHeader>
                        <CardTitle className="text-white text-lg flex items-center gap-2">
                            <Clock className="h-5 w-5 text-blue-400" />
                            Últimas Ações (Tickets)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats.personal.recentActions.length > 0 ? (
                            <div className="space-y-4">
                                {stats.personal.recentActions.map((ticket, index) => (
                                    <div key={ticket.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
                                        <div>
                                            <p className="text-white text-sm font-medium">#{ticket.id.slice(0, 8)} - {ticket.title}</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Update: {new Date(ticket.lastUpdate || ticket.createdAt).toLocaleString('pt-BR')}
                                            </p>
                                        </div>
                                        <Badge variant="outline" className={`bg-${ticket.status === 'resolvido' ? 'green' : 'blue'}-500/20 text-${ticket.status === 'resolvido' ? 'green' : 'blue'}-400 border-none`}>
                                            {ticket.status}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
                                <p>Nenhuma atividade recente encontrada.</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recommendations Section */}
                <Card className="bg-gradient-to-r from-blue-900/10 to-purple-900/10 backdrop-blur border border-blue-500/20 h-full">
                    <CardContent className="p-6 h-full flex flex-col justify-center">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-blue-500/20 rounded-xl">
                                <Award className="h-6 w-6 text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white mb-2">Resumo de Performance</h3>
                                <div className="space-y-2 text-sm text-gray-300">
                                    <p>• Você resolveu <strong>{stats.personal.resolutionRate}%</strong> dos tickets que assumiu.</p>
                                    <p>• Sua categoria mais ativa é <strong>{stats.personal.topCategory}</strong>.</p>
                                    <p>• Mantenha o foco em respostas rápidas e resoluções completas para melhorar suas estatísticas.</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Global Stats - High Rank Only */}
            {(isAdmin || isHead) && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                >
                    <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2 mt-8">
                        <Shield className="h-5 w-5 text-purple-400" />
                        Visão Geral da Staff (Admin/Head)
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card className="bg-[#0f1115]/80 backdrop-blur border border-white/10">
                            <CardHeader>
                                <CardTitle className="text-white text-sm uppercase tracking-wider font-medium">
                                    Status dos Tickets (Hoje)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Total Abertos</span>
                                        <Badge className="bg-green-500/20 text-green-400 border-none">
                                            {stats.global.totalOpen}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Resolvidos (24h)</span>
                                        <Badge className="bg-blue-500/20 text-blue-400 border-none">
                                            {stats.global.totalSolved24h}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-400">Punições (24h)</span>
                                        <Badge className="bg-red-500/20 text-red-400 border-none">
                                            {stats.global.totalPunishments24h}
                                        </Badge>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-[#0f1115]/80 backdrop-blur border border-white/10 md:col-span-2">
                            <CardHeader>
                                <CardTitle className="text-white text-sm uppercase tracking-wider font-medium">
                                    Top Staff (Performance)
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="flex items-center justify-center h-40 text-gray-500 text-sm">
                                <TrendingUp className="h-5 w-5 mr-2 opacity-50" />
                                Dados insuficientes para ranking detalhado.
                            </CardContent>
                        </Card>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default StaffStatistics;
